import { useEffect, useMemo, useState } from 'react';
import { useChannelsStore } from '@/store/useChannelsStore';
import { usePlayerStore } from '@/store/usePlayerStore';
import { ChannelList } from '@/components/channel/ChannelList';
import { PageHeader } from '@/components/common/PageHeader';
import { sortChannelsByRelevance, searchChannels } from '@/lib/channels';
import { List, Search, Loader2, Tv } from 'lucide-react';

export function AllChannelsPage() {
  const {
    allChannels,
    allIndex,
    isLoadingAll,
    loadAll,
  } = useChannelsStore();
  const setCurrentChannel = usePlayerStore((s) => s.setCurrentChannel);
  const currentChannel = usePlayerStore((s) => s.currentChannel);

  const [query, setQuery] = useState('');

  // Auto-load all channels on first visit
  useEffect(() => {
    if (!allChannels && !isLoadingAll) {
      loadAll();
    }
  }, [allChannels, isLoadingAll, loadAll]);

  const sorted = useMemo(() => {
    if (!allChannels) return [];
    const filtered = query.trim()
      ? searchChannels(allIndex!, allChannels, query, 200)
      : allChannels;
    return sortChannelsByRelevance(filtered);
  }, [allChannels, allIndex, query]);

  // For the "no query" state, just show the first ~200 English-name channels
  // to avoid rendering 10k+ rows in the virtualizer at once
  const displayChannels = useMemo(() => {
    if (query.trim()) return sorted;
    return sorted.slice(0, 200);
  }, [sorted, query]);

  if (isLoadingAll) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-text-muted">
        <Loader2 className="w-10 h-10 animate-spin mb-4" />
        <p className="font-medium">Loading 10,000+ channels...</p>
        <p className="text-xs mt-1 opacity-60">This happens once and is cached</p>
      </div>
    );
  }

  if (!allChannels) {
    return (
      <div className="text-center py-12">
        <button
          onClick={loadAll}
          className="px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-hover transition-colors"
        >
          Load all 10k+ channels
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 h-full flex flex-col">
      <PageHeader
        icon={<List className="w-7 h-7 text-text-muted" />}
        title="All Channels"
        subtitle={`${allChannels.length.toLocaleString()} channels total · search to find specific broadcasters`}
      />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search 10,000+ channels..."
          className="w-full h-10 pl-10 pr-4 bg-bg-elevated border border-border rounded-lg text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </div>

      {query && (
        <p className="text-xs text-text-muted">
          {displayChannels.length} match{displayChannels.length === 1 ? '' : 'es'} for "{query}"
        </p>
      )}

      {!query && displayChannels.length < allChannels.length && (
        <p className="text-xs text-text-muted">
          Showing the first 200 channels. Use search to find specific ones.
        </p>
      )}

      <div className="flex-1 min-h-0">
        {displayChannels.length === 0 ? (
          <div className="text-center py-12 text-text-muted">
            <Tv className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No channels match "{query}"</p>
          </div>
        ) : (
          <ChannelList
            channels={displayChannels}
            onChannelClick={setCurrentChannel}
            activeChannelId={currentChannel?.id}
          />
        )}
      </div>
    </div>
  );
}